import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from "../../api/axios";
import PropTypes from 'prop-types';
import seedrandom from 'seedrandom';
import bingoBallDefault from '../../assets/bingo-ball.png';
import bingoBall1 from '../../assets/bingo-ball-1.png';
import bingoBall2 from '../../assets/bingo-ball-2.png';
import bingoBall3 from '../../assets/bingo-ball-3.png';
import bingoBall4 from '../../assets/bingo-ball-4.png';
import bingoBall5 from '../../assets/bingo-ball-5.png';
import { getPatternCells } from '../../utils/patternUtils';
import { getImageFromCache, storeImageInCache } from '../../utils/imageCache';
import { motion } from "framer-motion";
import { useFunctionalSounds } from '../../components/useFunctionalSounds';



import { preloadAllSounds } from "../../utils/soundPreloader";
import Confetti from "react-confetti";
import { useCallSounds } from "../../components/useCallSounds";


import '../../styles/BingoBoard.css';
import { MdPlayArrow, MdRestartAlt, MdPause } from 'react-icons/md';
import { MdGridView } from "react-icons/md";
import { FaCheck, FaEye } from "react-icons/fa";
import { MdCleaningServices } from 'react-icons/md';
import '../../styles/showCardDetails.css';
import { useNavigate  } from "react-router-dom";

import {
  CheckCircle,
  LoaderCircle,
} from "lucide-react";

const bingoColumns = {
  B: Array.from({ length: 15 }, (_, i) => i + 1),
  I: Array.from({ length: 15 }, (_, i) => i + 16),
  N: Array.from({ length: 15 }, (_, i) => i + 31),
  G: Array.from({ length: 15 }, (_, i) => i + 46),
  O: Array.from({ length: 15 }, (_, i) => i + 61),
};

const BingoDashboard = ({
  calledNumbers = [],
  setCalledNumbers,
  bingoCards = [],
  winnerAmount = 0,
  selectedCardIds = [],
  setSelectedCardIds, 
  userId = "",
  commissionPercent = 0,
  eachCardAmount = 0,
  cardCount = 0,
  onCommissionDeducted = () => {},
  topbarRef,
  resetGameState,
}) => {

  const [isStarting, setIsStarting] = useState(false);
  const [isGameRunning, setIsGameRunning] = useState(false);
  const [isPaused, setIsPaused] = useState(false);
  const [currentNumber, setCurrentNumber] = useState(null);
  const [inputCardId, setInputCardId] = useState('');
  const [result, setResult] = useState('');
  const [winningCards, setWinningCards] = useState([]);
  const [showCardModal, setShowCardModal] = useState(false);
  const [selectedCard, setSelectedCard] = useState(null);
  const rngRef = useRef(seedrandom(Date.now().toString()));
  const dashboardRef = useRef(null);
  const hasSavedSummary = useRef(false);
  const [animateBall, setAnimateBall] = useState(false);
  const [startMessage, setStartMessage] = useState('');
  const totalPrizePool = cardCount * eachCardAmount;
  const commission = (commissionPercent / 100) * totalPrizePool;
  const winnerAmountCalculated = totalPrizePool - commission;
  const [gameStartTrigger, setGameStartTrigger] = useState(0);
  const [highlightedNumber, setHighlightedNumber] = useState(null);
  const [highlightedNumbers, setHighlightedNumbers] = useState([]);
  const [checkingNumbers, setCheckingNumbers] = useState(false);
  const navigate = useNavigate();
  const [showConfirmClean, setShowConfirmClean] = useState(false);
  const [isFirstFourWinner, setIsFirstFourWinner] = useState(false);
  const [showMessage, setShowMessage] = useState(false);
  const [ballImageUrl, setBallImageUrl] = useState(bingoBallDefault);
  const [isLoading, setIsLoading] = useState(false);
  

  const [soundLoading, setSoundLoading] = useState(true);
  const [soundProgress, setSoundProgress] = useState(0);
  const [showSoundNotification, setShowSoundNotification] = useState(false);

const speedOptions = [
  { value: 2300, label: 'በቂ' },
  { value: 2150, label: 'ፈጣን' },
  { value: 2000, label: 'በጣም ፈጣን' },
];

const languages = [
  { code: "am", label: "ሀሁ ወንድ" },
  { code: "en", label: "አራዳ ወንድ" },
  { code: "or", label: "አፍሪካ ወንድ" },
];

const [language, setLanguage] = useState(() => {
  return localStorage.getItem("bingoLanguage") || "am";
});

const [callSpeed, setCallSpeed] = useState(() => {
  return parseInt(localStorage.getItem("bingoCallSpeed")) || 2000;
});

const [isLangDropdownOpen, setLangDropdownOpen] = useState(false);
const [isSpeedDropdownOpen, setSpeedDropdownOpen] = useState(false);

// Functional sounds
const { playPause, playReady, playClean, playSuccess, playFail } = useFunctionalSounds(language);

// Number call sounds
const { playCall } = useCallSounds(language);

// Example: play number sound
const handleNumberCall = (num) => {
  playCall(num);
};


const getBingoBallImage = useCallback(async () => {
  if (!isGameRunning || currentNumber === null) return bingoBallDefault;

  let key;
  if (currentNumber >= 1 && currentNumber <= 15) key = 'bingoBall1';
  else if (currentNumber >= 16 && currentNumber <= 30) key = 'bingoBall2';
  else if (currentNumber >= 31 && currentNumber <= 45) key = 'bingoBall3';
  else if (currentNumber >= 46 && currentNumber <= 60) key = 'bingoBall4';
  else if (currentNumber >= 61 && currentNumber <= 75) key = 'bingoBall5';
  else key = 'default';

  const cachedBlob = await getImageFromCache(key);
  if (cachedBlob) {
    return URL.createObjectURL(cachedBlob);
  }

  const imageMap = {
    bingoBall1,
    bingoBall2,
    bingoBall3,
    bingoBall4,
    bingoBall5,
    default: bingoBallDefault,
  };

  const imageUrl = imageMap[key];
  const response = await fetch(imageUrl);
  const blob = await response.blob();

  await storeImageInCache(key, blob);

  return URL.createObjectURL(blob);
}, [currentNumber, isGameRunning]);

const [highlighted, setHighlighted] = useState([]);
  const [patternIndex, setPatternIndex] = useState(0);

  const fullRow = (i) => Array.from({ length: 5 }, (_, j) => i * 5 + j);
  const fullCol = (i) => Array.from({ length: 5 }, (_, j) => i + j * 5);

  const patterns = [
    fullRow(2),       
    fullCol(2),       
    [0, 6, 12, 18, 24], 
    [4, 8, 12, 16, 20], 
    [0, 4, 20, 24],    
  ];

useEffect(() => {
  const loadSounds = async () => {
    const alreadyLoaded =
      localStorage.getItem("allSoundsLoaded");

    // SHOW NOTIFICATION
    setShowSoundNotification(true);

    if (alreadyLoaded === "true") {
      setSoundLoading(false);
      setSoundProgress(100);

      setTimeout(() => {
        setShowSoundNotification(false);
      }, 2500);

      return;
    }

    try {
      await preloadAllSounds((progress) => {
        setSoundProgress(progress);
      });

      localStorage.setItem(
        "allSoundsLoaded",
        "true"
      );

      setSoundLoading(false);
      setSoundProgress(100);

      setTimeout(() => {
        setShowSoundNotification(false);
      }, 2500);

    } catch (err) {
      console.error(err);

      setShowSoundNotification(false);
    }
  };

  loadSounds();
}, []);

useEffect(() => {
  const interval = setInterval(() => {
    setHighlighted(patterns[patternIndex]);
    setPatternIndex((prev) => (prev + 1) % patterns.length);
  }, callSpeed);

  return () => clearInterval(interval); 
}, [patternIndex, callSpeed]); 

  const initializeGame = useCallback(() => {
    const seed = Date.now().toString();
    rngRef.current = seedrandom(seed);
    setCalledNumbers([]);
    setCurrentNumber(null);
    setWinningCards([]);
    setResult('');
    setIsPaused(false);
  }, [setCalledNumbers]);

  const calculateAdjustedWinningAmount = () => {
  if (winningCards.length === 0) return winnerAmount;
  return winnerAmount;
};
const handleLanguageChange = (langCode) => {
  setLanguage(langCode);
  localStorage.setItem("bingoLanguage", langCode);
  setLangDropdownOpen(false);
};

const handleSpeedChange = (newSpeed) => {
  setCallSpeed(newSpeed);
  localStorage.setItem("bingoCallSpeed", newSpeed);
};



const callNumber = useCallback(() => {
  if (!isGameRunning || isPaused) return;

  if (calledNumbers.length === 75) {
    setIsGameRunning(false);
    setCurrentNumber(null); 
    return;
  }

  let randomNumber;
  do {
    randomNumber = Math.floor(rngRef.current() * 75) + 1;
  } while (calledNumbers.includes(randomNumber));

  setCalledNumbers(prev => [...prev, randomNumber]);
  setCurrentNumber(randomNumber);

  playCall(randomNumber);

  setAnimateBall(true);
  setTimeout(() => setAnimateBall(false), 500);
}, [isGameRunning, isPaused, calledNumbers, playCall, setCalledNumbers, callSpeed]);


const saveSummaryToBackend = async (winningCardIds) => {
  try {
    const payload = {
      calledNumbers,
      winningCardIds,
      winnerAmount,
      userId,
      commissionPercent,
      eachCardAmount,
      cardCount,
      initial: true,
    };

    await axios.post("/game-results/save-summary", payload);

  } catch (error) {
    console.error("❌ Failed to save summary:", error?.response?.data || error.message);
    setStartMessage("⛔ ውጤት ማስቀመጥ አልተቻለም።");
  }
};

const isWinnerCard =
  selectedCard &&
  winningCards.includes(selectedCard.id);

useEffect(() => {
  const loadImage = async () => {
    const imgUrl = await getBingoBallImage();
    setBallImageUrl(imgUrl);
  };
  loadImage();
}, [currentNumber, isGameRunning]);

  useEffect(() => {
    let interval;
    if (isGameRunning && !isPaused) {
      interval = setInterval(callNumber, callSpeed);
    }
    return () => clearInterval(interval);
  }, [isGameRunning, isPaused, callNumber, callSpeed, gameStartTrigger]);


const getPatternsAndLogic = () => {
  if (!topbarRef.current) return { patterns: [], useAnd: false };
  return {
    patterns: topbarRef.current.getSelectedPatterns?.() || [],
    useAnd: topbarRef.current.getUseAndLogic?.() || false,
  };
};
const addBounce = (e) => {
  e.target.classList.add("btn-bounce");

  setTimeout(() => {
    e.target.classList.remove("btn-bounce");
  }, 300);
};


const closeCardModal = () => {
  setShowCardModal(false);
  setSelectedCard(null);
};

const deductCommission = async () => {
  const totalPrizePool = cardCount * eachCardAmount;
  const commission = (commissionPercent / 100) * totalPrizePool;

  if (!userId || commission <= 0) {
    console.warn("❌ Invalid commission deduction input", { userId, commission });
    return false;
  }

  try {

    await axios.post("/auth/support/deduct", { userId, amount: commission });

    if (topbarRef?.current?.fetchBalance) {
      topbarRef.current.fetchBalance();
    }

    if (typeof onCommissionDeducted === "function") {
      onCommissionDeducted();
    }

    return true;

  } catch (error) {
    const message = error?.response?.data?.message || error.message;
    console.error("❌ Commission deduction failed:", message);
    setStartMessage(`⛔ ኮሚሽኑን መቀነስ አልተቻለም። ${message}`);
    return false;
  }
};

const startGame = async () => {
  if (!isGameRunning && !hasSavedSummary.current) {
    if (!userId || commissionPercent <= 0 || cardCount <= 1 || eachCardAmount <= 5) {
      setStartMessage("⛔ ጨዋታ ለማስጀመር ደራሽ ያዘጋጁ! እንደገና ይሞክሩ።");
      return;
    }

    const currentBalance = topbarRef?.current?.getBalance?.();
    const totalPrizePool = cardCount * eachCardAmount;
    const commission = (commissionPercent / 100) * totalPrizePool;

    if (!navigator.onLine) {
 
  setStartMessage("📡 እባክዎን ኢንተርኔት ያገናኙ !");
  return;
}

if (currentBalance === 0 || currentBalance < commission) {

  
  return;
}
    try {
      const success = await deductCommission();
      if (!success) {
        setStartMessage("⛔ ጨዋታ ለማስጀመር ደራሽ ያዘጋጁ! እንደገና ይሞክሩ");
        return;
      }
      initializeGame();

      setTimeout(() => {
        setIsGameRunning(true);
        setGameStartTrigger((prev) => prev + 1);
      }, 50);

      hasSavedSummary.current = true;
      const payload = {
        cardCount,
        commissionPercent,
        calledNumbers: [],
        winningCardIds: [],
        userId,
        eachCardAmount,
        initial: true,
      };

      await axios.post("/game-results/save-summary", payload);
    } catch (error) {
      const message = error?.response?.data?.message || error.message;
      console.error("❌ Summary save failed:", message);
      setStartMessage(`⛔ ውጤት ማስቀመጥ አልተቻለም። ${message}`);
    }
  } else if (isPaused) {
    setIsPaused(false);
  }
};

const restartGame = () => {
  initializeGame();
  hasSavedSummary.current = false;

  setWinningCards([]);
  setCurrentNumber(null);
  setResult('');
  setInputCardId('');
  setIsPaused(false);
  if (typeof setCalledNumbers === 'function') setCalledNumbers([]);
  setIsGameRunning(true);
};
  const cleanGame = () => {
  setIsGameRunning(false);
  setIsPaused(false);
  setCalledNumbers([]);
  setCurrentNumber(null);
  setWinningCards([]);
  setResult('');
  resetGameState();
  hasSavedSummary.current = false

};
  const pauseGame = () => {
     playPause(); 
    setIsPaused(true);
  };

const goToBingoCardPage = () => {
  navigate("/BingoCartela", {
    state: {
      winningCardIds: winningCards,
      calledNumbers,
      userId,
    },
  });}

const verifyCard = (card) => {
  const { card: cardNumbers } = card;
  const { patterns: selectedPatterns, useAnd: useAndLogic } = getPatternsAndLogic();


  const isNumberCalled = (num) => num === 0 || calledNumbers.includes(num);

  const checkDefaultWin = () => {
    const horizontalWin = cardNumbers.some(row => row.every(isNumberCalled));
    const verticalWin = cardNumbers[0].some((_, colIndex) =>
      cardNumbers.every(row => isNumberCalled(row[colIndex]))
    );
    const diagonal1 = cardNumbers.every((row, idx) => isNumberCalled(row[idx]));
    const diagonal2 = cardNumbers.every((row, idx) => isNumberCalled(row[4 - idx]));
    const cornersWin =
      isNumberCalled(cardNumbers[0][0]) &&
      isNumberCalled(cardNumbers[0][4]) &&
      isNumberCalled(cardNumbers[4][0]) &&
      isNumberCalled(cardNumbers[4][4]);

    return horizontalWin || verticalWin || diagonal1 || diagonal2 || cornersWin;
  };

const checkPattern = (patternName) => {
  const patternGroups = getPatternCells(patternName);
  if (!patternGroups || patternGroups.length === 0) return false;

  return patternGroups.some(group =>
    group.every(cellIndex => {
      const row = Math.floor(cellIndex / 5);
      const col = cellIndex % 5;
      return isNumberCalled(cardNumbers[row][col]);
    })
  );
};
  if (
    selectedPatterns &&
    Array.isArray(selectedPatterns) &&
    selectedPatterns.length > 0
  ) {
    const matchedPatterns = selectedPatterns.filter(pattern => checkPattern(pattern));

    const isWinner = useAndLogic
      ? matchedPatterns.length === selectedPatterns.length
      : matchedPatterns.length > 0;                      

     return { isWinner, winType: 'pattern', matchedPatterns };

  }

  return { isWinner: checkDefaultWin(), winType: 'default' };
};

const isWinningNumber = (cardNumbers, rowIndex, colIndex, calledNumbers) => {
  const row = cardNumbers.map(col => col[rowIndex]);
  const colVals = cardNumbers[colIndex];

  const isRowWin = row.every(num => calledNumbers.includes(num));
  const isColWin = colVals.every(num => calledNumbers.includes(num));
  const isDiagonal1 =
    rowIndex === colIndex &&
    [0, 1, 2, 3, 4].every(i => calledNumbers.includes(cardNumbers[i][i]));
  const isDiagonal2 =
    rowIndex + colIndex === 4 &&
    [0, 1, 2, 3, 4].every(i => calledNumbers.includes(cardNumbers[i][4 - i]));
  const isCorner =
    ((rowIndex === 0 && colIndex === 0) ||
      (rowIndex === 0 && colIndex === 4) ||
      (rowIndex === 4 && colIndex === 0) ||
      (rowIndex === 4 && colIndex === 4)) &&
    [cardNumbers[0][0], cardNumbers[0][4], cardNumbers[4][0], cardNumbers[4][4]].every(num =>
      calledNumbers.includes(num)
    );

  return isRowWin || isColWin || isDiagonal1 || isDiagonal2 || isCorner;
};
useEffect(() => {
  if (startMessage) {
    setShowMessage(true); 

    const timer = setTimeout(() => {
      setShowMessage(false);
      setTimeout(() => setStartMessage(''), 500);
    }, 5000);

    return () => clearTimeout(timer);
  }
}, [startMessage]);

const handleCheck = () => {
  playClean(); 
};

const handleMix = () => {
  playReady();

  let count = 0;
  const interval = setInterval(() => {
    const numbers = new Set();
    while (numbers.size < 5) {
      numbers.add(Math.floor(Math.random() * 75) + 1);
    }

    const selected = Array.from(numbers);
    setHighlightedNumbers(selected);

    count++;
    if (count >= 20) {
      clearInterval(interval);
      setTimeout(() => {
        setHighlightedNumbers([]);
      }, 1000); 
    }
  }, 150);
};


const verifyAndShowCard = () => {
  const cardId = parseInt(inputCardId);

  if (!inputCardId || isNaN(cardId)) {
    setStartMessage('አባክዎ የሚያገለግል ካርቴላ ቁጥር ያስገቡ!');
    playFail();
    return;
  }

  if (!selectedCardIds.includes(cardId)) {
    setStartMessage(`ካርቴላ ${cardId} አልተመረጠም!`);
    playFail();
    return;
  }

  const card = bingoCards.find(c => c.id === cardId);
  if (!card) {
    setStartMessage('ምንም ካርቴላ ቁጥር አልተገኘም!');
    playFail();
    return;
  }

  const { isWinner, winType, matchedPatterns } = verifyCard(card);
  const winAmount = calculateAdjustedWinningAmount();

if (isWinner) {
  const patternText =
    winType === 'pattern'
      ? ` pattern(s): ${matchedPatterns?.join(", ") || 'default'} `
      : '';

  setStartMessage(
    `🎉 ቢንጎ! ካርቴላ ${cardId} አሸንፏል ${patternText}የሽልማት መጠን ${winAmount} ብር`
  );

  playSuccess();

  if (!winningCards.includes(cardId)) {
    const newWinners = [...winningCards, cardId];
    setWinningCards(newWinners);
    saveSummaryToBackend(newWinners);
  }
} else {
  setStartMessage(`❌ ካርቴላ ${cardId} አላሸነፈም`);
  playFail();
}


  setSelectedCard(card);

  const firstFour = calledNumbers.slice(0, 4);

  const checkFirstFourWin = () => {
    const { card: cardNumbers } = card;
    const isNumberCalled = (num) => num === 0 || firstFour.includes(num);

    const horizontalWin = cardNumbers.some(row => row.every(isNumberCalled));
    const verticalWin = cardNumbers[0].some((_, colIndex) =>
      cardNumbers.every(row => isNumberCalled(row[colIndex]))
    );
    const diagonal1 = cardNumbers.every((row, idx) => isNumberCalled(row[idx]));
    const diagonal2 = cardNumbers.every((row, idx) => isNumberCalled(row[4 - idx]));
    const cornersWin =
      isNumberCalled(cardNumbers[0][0]) &&
      isNumberCalled(cardNumbers[0][4]) &&
      isNumberCalled(cardNumbers[4][0]) &&
      isNumberCalled(cardNumbers[4][4]);

    return horizontalWin || verticalWin || diagonal1 || diagonal2 || cornersWin;
  };

  setIsFirstFourWinner(checkFirstFourWin());
  setShowCardModal(true);
};

  useEffect(() => {
    if (selectedCardIds.length > 0 && calledNumbers.length > 0) {
      const newWinners = [];
      bingoCards.forEach(card => {
        const result = verifyCard(card);
if (selectedCardIds.includes(card.id) && result?.isWinner) {
  newWinners.push(card.id);
}

      });
      setWinningCards(newWinners);
    }
  }, [calledNumbers, selectedCardIds, bingoCards]);

  const unselectCard = (cardId) => {
  const updated = selectedCardIds.filter(id => id !== cardId);
  setSelectedCardIds(updated);
  setStartMessage(`🚫 ካርቴላ ${cardId} ታስሯል!`);
};


  return (
    
    
    <div className="bingo-dashboard" ref={dashboardRef}>
      
<div className="bingo-status-column">

{showSoundNotification && (
  <div className="sound-toast">
    
    {soundLoading ? (
      <>
        <LoaderCircle className="sound-toast-icon spinning" size={22} />
        
        <div className="sound-toast-content">
          <span className="sound-toast-title">
            Loading Sounds
          </span>

          <span className="sound-toast-subtitle">
            {soundProgress}% completed
          </span>

          <div className="sound-loading-bar">
            <div
              className="sound-loading-fill"
              style={{ width: `${soundProgress}%` }}
            />
          </div>
        </div>
      </>
    ) : (
      <>
        <CheckCircle className="sound-toast-icon success" size={22} />

        <div className="sound-toast-content">
          <span className="sound-toast-title">
            Sounds Ready
          </span>

          <span className="sound-toast-subtitle">
           sounds save successfully
          </span>
        </div>

      </>
    )}
  </div>
)}
  

  {/* =========================
      1. Bingo Play
  ========================= */}
  <div className="bingo-play-header">
    <div className="board">
      {Array.from({ length: 25 }, (_, i) => (
        <div
          key={i}
          className={`cell ${
            highlighted.includes(i)
              ? "highlight"
              : ""
          }`}
        />
      ))}
    </div>
  </div>

  {/* =========================
      2. Winner Info
  ========================= */}
  <div className="winner-info">

    <button className="winner-button">
      {winnerAmount > 0
        ? calculateAdjustedWinningAmount().toFixed()
        : ""}
      {" "}ደራሽ
    </button>

    <div className="win-now-text">

      <div className="verify-section pop-style">

        <input
          type="number"
          className="winner-amount-inputt"
          placeholder="ካርቴላ ቁጥር"
          value={inputCardId}
          min="1"
          onChange={(e) =>
            setInputCardId(e.target.value)
          }
        />

        <button
          className="winner-check"
          onClick={(e) => {
            addBounce(e);
            verifyAndShowCard();
          }}
          disabled={
            !inputCardId ||
            isNaN(parseInt(inputCardId))
          }
        >
          <FaCheck style={{ marginRight: "8px" }} />
          አረጋግጥ
        </button>

        {result && (
          <p className="signin-error">
            {result}
          </p>
        )}

      </div>
    </div>
  </div>

  {/* =========================
      3. Bingo Ball
  ========================= */}
  <div
    className={`bingo-ball-wrapper ${
      animateBall ? "animated" : ""
    }`}
    style={{
      backgroundImage: `url(${ballImageUrl})`,
      backgroundSize: "contain",
      backgroundRepeat: "no-repeat",
      backgroundPosition: "center center",
      width: "240px",
      height: "240px",
      marginTop: -85,
      marginLeft: 75,
    }}
  >
    <div className="bingo-ball-number">
      {currentNumber !== null
        ? currentNumber
        : ""}
    </div>
  </div>
</div>

{startMessage && (
  <div className={`message-container ${showMessage ? 'show' : ''}`}>
    {startMessage}
  </div>
)}


      <div className="game-board pop-style">
        <div className="bingo-letters">
          {['B', 'I', 'N', 'G', 'O'].map((letter) => (
            <div key={letter} className="bingo-letter">
              {letter}
            </div>
          ))}
        </div>

        {Array.from({ length: 15 }).map((_, rowIndex) => (
          <div key={rowIndex} className="board-row">
            {['B', 'I', 'N', 'G', 'O'].map((letter, colIndex) => {
              const number = bingoColumns[letter][rowIndex];
              return (
                <div
                  key={colIndex}
                 className={`board-cell 
                  ${calledNumbers?.includes?.(number) ? 'called pop' : ''} 
                  ${highlightedNumbers.includes(number) ? 'flashing' : ''}`}
                >
                  {number}
                </div>
              );
            })}
          </div>
        ))}
      </div>
      <div className="game-controls pop-style">
        <div className="control-buttons">
<button
  className="button_board"
  onClick={goToBingoCardPage}
  disabled={isStarting || isGameRunning}  
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    opacity: (isStarting || isGameRunning) ? 0.5 : 1,
    cursor: (isStarting || isGameRunning) ? 'not-allowed' : 'pointer'
  }}
>
  <MdGridView style={{ marginRight: '8px' }} />
  ካርቴላ ምረጥ
</button>

            <button
  className={`button_board ${isGameRunning ? 'active' : ''}`}
  onClick={(e) => {
    addBounce(e);    
    setIsStarting(true);
    setTimeout(() => {
      startGame();
      setIsStarting(false);
    }, 1500);
  }}
  style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
>
  {isStarting ? (
    <div className="loading-spinner"></div>
  ) : (
    <MdPlayArrow />
  )}
  {calledNumbers.length === 0 ? 'ጀምር' : 'ቀጥል'}
</button>

<button
  className='button_board'
  onClick={(e) => { addBounce(e); pauseGame(); }}
  disabled={!isGameRunning || isPaused}
  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
>
  <MdPause />
  አቁም
</button>
<button
  className='button_board'
  onClick={() => setShowConfirmClean(true)}  
  disabled={calledNumbers.length === 0 && !isGameRunning}
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    opacity: (calledNumbers.length === 0 && !isGameRunning) ? 0.5 : 1,
    cursor: (calledNumbers.length === 0 && !isGameRunning) ? 'not-allowed' : 'pointer'
  }}
>
  <MdCleaningServices />
  ተዘጋጅ
</button>

<button
  className='button_board'
  onClick={(e) => { addBounce(e); restartGame(); }} 
  disabled={!isGameRunning && calledNumbers.length === 0}
  style={{ display: 'flex', alignItems: 'center', gap: '5px' }}
>
  <MdRestartAlt />
  እንደገና
</button>
<button
  className="button_board"
   onClick={(e) => { addBounce(e); handleCheck(); }} 
  disabled={isStarting || isGameRunning}  
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    opacity: (isStarting || isGameRunning) ? 0.5 : 1,
    cursor: (isStarting || isGameRunning) ? 'not-allowed' : 'pointer'
  }}
>
  <FaCheck />
  ዝግጁ!
</button>

<div className="language-selector">
      <button
        className="lang-button"
        onClick={() => setLangDropdownOpen(!isLangDropdownOpen)}
      >
        🌐 {languages.find(lang => lang.code === language)?.label}
        <svg
          xmlns="http://www.w3.org/2000/svg"
          viewBox="0 0 20 20"
          fill="currentColor"
          width="16"
          height="16"
          className={`transition-transform duration-200 ${isLangDropdownOpen ? 'rotate-180' : 'rotate-0'}`}
        >
          <path fillRule="evenodd" d="M5.23 7.21a.75.75 0 011.06.02L10 11.584l3.71-4.354a.75.75 0 111.14.976l-4.25 5a.75.75 0 01-1.14 0l-4.25-5a.75.75 0 01.02-1.06z" clipRule="evenodd" />
        </svg>
      </button>

      {isLangDropdownOpen && (
        <div className="dropdown animated-fade">
          {languages.map(lang => (
            <button
              key={lang.code}
              onClick={() => handleLanguageChange(lang.code)}
              className={`dropdown-item ${language === lang.code ? 'selected' : ''}`}
            >
              {lang.label}
            </button>
          ))}
        </div>
      )}
    </div>



<button
  className="button_board"
   onClick={(e) => { addBounce(e); handleMix(); }} 
  disabled={isStarting || isGameRunning}  
  style={{
    display: 'flex',
    alignItems: 'center',
    gap: '5px',
    opacity: (isStarting || isGameRunning) ? 0.5 : 1,
    cursor: (isStarting || isGameRunning) ? 'not-allowed' : 'pointer'
  }}
>
  Shuffle
</button>

        </div>
<div className="speed-control">

<span className="speed-value">ፍጥነት</span>

  {/* Slider for fine-tuning */}
  <input
    type="range"
    id="speed-slider"
    min="1600"
    max="4200"
    step="10"
    value={callSpeed}
    onChange={(e) => handleSpeedChange(Number(e.target.value))}
  />

  {/* Show selected speed in seconds */}
  <span className="speed-value">{(callSpeed / 1000).toFixed(1)} </span>
</div>


      </div>
      {showConfirmClean && (
  <div
    className="confirm-modal-overlay"
    onClick={() => setShowConfirmClean(false)}
  >
    <div className="confirm-modal" onClick={(e) => e.stopPropagation()}>
      <div className="confirm-modal-header">
        <h3> ያረጋግጡ?</h3>
      </div>

      <div className="confirm-modal-body">
        <p>
          የጨዋታው ውጤት ይጥፋ ፡ ለአዲስ ይዘጋጅ ። <br />
        </p>
      </div>

      <div className="confirm-modal-footer">
        <button
          className="confirm-btn yes-btn"
          onClick={() => {
            cleanGame();
            setShowConfirmClean(false);
          }}
        >
          አዎ
        </button>
        <button
          className="confirm-btn no-btn"
          onClick={(e) => { addBounce(e); setShowConfirmClean(false); }} 
        >
           አይ
        </button>
      </div>
    </div>
  </div>
)}

{showCardModal && selectedCard && (
  <div className="card-modal-overlay" onClick={closeCardModal}>

    <div
      className={`card-modal pop-style ${
        winningCards.includes(selectedCard.id)
          ? "winner-popup"
          : ""
      }`}
      onClick={(e) => e.stopPropagation()}
    >

      {/* 🎉 CONFETTI CELEBRATION */}
      {winningCards.includes(selectedCard.id) && (
        <div className="confetti-wrapper">
          {[...Array(80)].map((_, i) => (
            <span
              key={i}
              className="confetti-piece"
              style={{
                left: `${Math.random() * 100}%`,
                animationDelay: `${Math.random() * 2}s`,
                animationDuration: `${2 + Math.random() * 3}s`,
              }}
            />
          ))}

          <div className="winner-title">
            WINNER 
          </div>
        </div>
      )}

      <div className="card-modal-header">
        <h3>ካርቴላ {selectedCard.id}</h3>

        <button
          className="close-button"
          onClick={closeCardModal}
        >
          ×
        </button>
      </div>

      <div className="card-grid-container">

        {/* BINGO LETTERS */}
        <div className="card-row">
          {["B", "I", "N", "G", "O"].map((letter) => (
            <div
              key={letter}
              className="card-cell card-header-cell"
            >
              {letter}
            </div>
          ))}
        </div>

        {/* CARD NUMBERS */}
        {Array.from({ length: 5 }).map((_, rowIndex) => (
          <div key={rowIndex} className="card-row">

            {selectedCard.card.map((col, colIndex) => {
              const number = col[rowIndex];

              const isCenter =
                rowIndex === 2 && colIndex === 2;

              const isCalled =
                calledNumbers.includes(number);

              const isWinning = isWinningNumber(
                selectedCard.card,
                rowIndex,
                colIndex,
                calledNumbers
              );

              let cellClass = "cell-default";

              if (isCenter) {
                cellClass = "cell-free";
              } else if (isWinning) {
                cellClass = "cell-win";
              } else if (isCalled) {
                cellClass = "cell-called";
              }

              return (
                <div
                  key={colIndex}
                  className={`card-cell ${cellClass}`}
                >
                  {isCenter ? "ነጻ" : number}
                </div>
              );
            })}
          </div>
        ))}
      </div>

      {/* BONUS MESSAGE */}
      {isFirstFourWinner && (
        <div
          className="first-four-msg"
          style={{
            marginTop: "12px",
            fontWeight: "bold",
            color: "#00b40c",
            textAlign: "center",
            fontSize: "18px",
          }}
        >
          🎯 ይህ ካርቴላ በመጀመሪያ 4 ጥሪ
          የ1000 ብር ቦነስ ተሸልመዋል!!
        </div>
      )}

      {/* BLOCK CARD */}
      {!winningCards.includes(selectedCard.id) && (
        <button
          onClick={() => {
            unselectCard(selectedCard.id);
            setShowCardModal(false);
          }}
          className="popup-button"
        >
          🚫 ካርቴላው ይታሰር
        </button>
      )}
    </div>
  </div>
)}
    </div>
  );
};
BingoDashboard.propTypes = {
  calledNumbers: PropTypes.array.isRequired,
  setCalledNumbers: PropTypes.func.isRequired,
  bingoCards: PropTypes.array.isRequired,
  winnerAmount: PropTypes.number.isRequired,
  selectedCardIds: PropTypes.array.isRequired,
  setSelectedCardIds: PropTypes.func.isRequired, 
  userId: PropTypes.string.isRequired,
  commissionPercent: PropTypes.number.isRequired,
  eachCardAmount: PropTypes.number.isRequired,
  cardCount: PropTypes.number.isRequired,
  onCommissionDeducted: PropTypes.func,
};



export default BingoDashboard;
